// src/components/orders/ConfirmRefundDialog.jsx
import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

const REFUND_METHODS = [
  { value: 'bank_transfer', label: '银行转账' },
  { value: 'cash', label: '现金' },
  { value: 'other', label: '其他' },
];

const ConfirmRefundDialog = ({ order, onClose, onConfirm, submitting }) => {
  const orderTotal = parseFloat(order.total_amount || 0);
  const [refundAmount, setRefundAmount] = useState(orderTotal.toFixed(2));
  const [refundMethod, setRefundMethod] = useState('bank_transfer');
  const [refundNote, setRefundNote] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('请输入正确的退款金额');
      return;
    }
    if (amount > orderTotal) {
      setError('退款金额不能超过订单总额');
      return;
    }
    if (!refundMethod) {
      setError('请选择退款方式');
      return;
    }
    setError('');
    onConfirm({
      refund_amount: amount,
      refund_method: refundMethod,
      refund_note: refundNote.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="border-b p-5 flex justify-between items-center">
          <h3 className="text-lg font-bold">确认已退款</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">
              这一步不会自动转账，只是店家线下（银行转账/现金）把钱退给客户之后，来这里做记录、关闭订单。
              确认后订单会整单取消，商品库存会全部加回，此操作不可撤销。
            </p>
          </div>

          <div className="text-sm text-gray-500">
            订单号 <span className="text-gray-800 font-medium">{order.order_no}</span> ·
            订单总额 <span className="text-gray-800 font-medium">${orderTotal.toFixed(2)}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              退款金额 (USD) <span className="text-red-500">*</span>
            </label>
            <input
              type="number" step="0.01" min="0" max={orderTotal}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              value={refundAmount}
              onChange={e => setRefundAmount(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1">
              默认是订单全额，如果店家和客户协商后只退了部分金额，改这里就行——商品库存不受这个数字影响，会整单加回
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              退款方式 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {REFUND_METHODS.map(m => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setRefundMethod(m.value)}
                  className={`px-3 py-2 rounded-lg text-sm border ${
                    refundMethod === m.value
                      ? 'border-blue-500 bg-blue-50 text-blue-600 font-medium'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              备注 / 凭证号（选填）
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="比如转账流水号、经手人姓名，方便以后对账查询"
              value={refundNote}
              onChange={e => setRefundNote(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} disabled={submitting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            取消
          </button>
          <button onClick={handleConfirm} disabled={submitting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
            {submitting ? '提交中...' : '确认已退款'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmRefundDialog;